/**
 * Marvel LED Uploader Specifications
 * 
 * Complex uploader with multiple LED formats:
 * - Tiled: 3 levels (FOP, L2, L3), 1 file each, must end with _MRV-TL-{level}
 * - Overlay: 4 levels (FOP, L2, L2B, L3), 1 file each, must end with _MRV-OL-{level}
 * - 360: 3 levels, multiple segments each, must end with _MRV-{level}-{segment}
 * - Interchange: Single asset for all levels
 * - DJ Booth: Single asset
 * 
 * Key features:
 * - Filename validation (must end with segment ID)
 * - Duration consistency (all clips must match)
 * - Per-segment dimension validation
 * - Grouped asset tracking with status
 */

// ============================================================
// LED FORMAT OPTIONS (for format selector)
// ============================================================
export const LED_FORMATS = [
  { id: "tiled", label: "Tiled" },
  { id: "360", label: "360" },
  { id: "interchange", label: "Interchange" },
  { id: "overlay", label: "Overlay" },
  { id: "dj-booth", label: "DJ Booth" },
];

// ============================================================
// COMMON VIDEO SPEC (shared across LED types)
// ============================================================
const LED_VIDEO_COMMON = {
  allowed: true,
  frameRate: 50,
  extensions: ["mp4"],
  formats: ["AVC"],
  audio: { mode: "optional" },
  requiresAlpha: false,
  inspectAlpha: false,
};

// Overlay accepts MOV / ProRes 4444 (alpha channel content)
const LED_OVERLAY_VIDEO_COMMON = {
  ...LED_VIDEO_COMMON,
  extensions: ["mp4", "mov"],
  formats: ["AVC", "ProRes"],
  requiresAlpha: false,
  inspectAlpha: true,
  requireProRes4444: true, // ProRes must be 4444 profile — 422/HQ variants rejected
};

const LED_IMAGE_COMMON = {
  allowed: true,
  extensions: ["png", "jpg", "jpeg"],
  requiresAlpha: false,
  inspectAlpha: false,
};

// Duration ranges
const LED_DURATION_RANGE = { min: 5, max: 180 }; // 5s - 3:00
const INTERCHANGE_DURATION = 10; // Exact 10s
const DJ_BOOTH_DURATION = 30; // Exact 30s

// ============================================================
// TILED LED SPECS (3 levels, 1 file each)
// ============================================================
export const TILED_SPECS = {
  "MRV-TL-FOP": {
    label: "Marvel Tiled LED – Field of Play",
    uploadId: "MRV-TL-FOP",
    ledType: "tiled",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-TL-FOP\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-TL-FOP (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 2370, height: 96 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 2370,
      height: 96,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 2370,
      height: 96,
    },
  },
  
  "MRV-TL-L2": {
    label: "Marvel Tiled LED – Level 2",
    uploadId: "MRV-TL-L2",
    ledType: "tiled",
    level: "L2",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-TL-L2\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-TL-L2 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 2400, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 2400,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 2400,
      height: 90,
    },
  },
  
  "MRV-TL-L3": {
    label: "Marvel Tiled LED – Level 3",
    uploadId: "MRV-TL-L3",
    ledType: "tiled",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-TL-L3\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-TL-L3 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1850, height: 90 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 1850,
      height: 90,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1850,
      height: 90,
    },
  },
};

// ============================================================
// OVERLAY LED SPECS (4 levels, 1 file each)
// ============================================================
export const OVERLAY_SPECS = {
  "MRV-OL-FOP": {
    label: "Marvel Overlay LED – Field of Play",
    uploadId: "MRV-OL-FOP",
    ledType: "overlay",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-OL-FOP\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-OL-FOP (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 96 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 96,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 96,
    },
  },
  
  "MRV-OL-L2": {
    label: "Marvel Overlay LED – Level 2",
    uploadId: "MRV-OL-L2",
    ledType: "overlay",
    level: "L2",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-OL-L2\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-OL-L2 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 80 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 80,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 80,
    },
  },
  
  "MRV-OL-L2B": {
    label: "Marvel Overlay LED – Level 2 Camera Basket",
    uploadId: "MRV-OL-L2B",
    ledType: "overlay",
    level: "L2B",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-OL-L2B\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-OL-L2B (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 80 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 80,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 80,
    },
  },
  
  "MRV-OL-L3": {
    label: "Marvel Overlay LED – Level 3",
    uploadId: "MRV-OL-L3",
    ledType: "overlay",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-OL-L3\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-OL-L3 (before the extension)",
    durationRange: LED_DURATION_RANGE,
    
    dimensions: [{ width: 1920, height: 60 }],
    
    videoSpec: {
      ...LED_OVERLAY_VIDEO_COMMON,
      width: 1920,
      height: 60,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1920,
      height: 60,
    },
  },
};

// ============================================================
// 360 LED SPECS (3 levels, multiple segments each)
// ============================================================
export const THREE_SIXTY_SPECS = {
  "MRV-FOP": {
    label: "Marvel 360 LED – Field of Play",
    uploadId: "MRV-FOP",
    ledType: "360",
    level: "FOP",
    contentGroup: "groupedAssets",
    maxFiles: 5,
    requiredFiles: 5,
    durationRange: LED_DURATION_RANGE,
    filenameHint: "Filename must end with one of: _MRV-FOP-1, _MRV-FOP-2, _MRV-FOP-3, _MRV-FOP-4, _MRV-FOP-5",
    
    // Common video spec (framerate, codec, etc)
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    // Per-segment dimensions
    segments: {
      "MRV-FOP-1": { width: 9000, height: 96 },
      "MRV-FOP-2": { width: 9000, height: 96 },
      "MRV-FOP-3": { width: 11520, height: 96 },
      "MRV-FOP-4": { width: 9000, height: 96 },
      "MRV-FOP-5": { width: 9000, height: 96 },
    },
  },
  
  "MRV-L2": {
    label: "Marvel 360 LED – Level 2",
    uploadId: "MRV-L2",
    ledType: "360",
    level: "L2",
    contentGroup: "groupedAssets",
    maxFiles: 6,
    requiredFiles: 6,
    durationRange: LED_DURATION_RANGE,
    filenameHint: "Filename must end with one of: _MRV-L2-1 through _MRV-L2-6",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MRV-L2-1": { width: 6336, height: 80 },
      "MRV-L2-2": { width: 13344, height: 80 },
      "MRV-L2-3": { width: 13344, height: 80 },
      "MRV-L2-4": { width: 6336, height: 80 },
      "MRV-L2-5": { width: 6912, height: 80 },
      "MRV-L2-6": { width: 6912, height: 80 },
    },
  },
  
  "MRV-L3": {
    label: "Marvel 360 LED – Level 3",
    uploadId: "MRV-L3",
    ledType: "360",
    level: "L3",
    contentGroup: "groupedAssets",
    maxFiles: 4,
    requiredFiles: 4,
    durationRange: LED_DURATION_RANGE,
    filenameHint: "Filename must end with one of: _MRV-L3-1, _MRV-L3-2, _MRV-L3-3, _MRV-L3-4",
    
    commonVideoSpec: LED_VIDEO_COMMON,
    imageSpec: LED_IMAGE_COMMON,
    
    segments: {
      "MRV-L3-1": { width: 14910, height: 60 },
      "MRV-L3-2": { width: 14910, height: 60 },
      "MRV-L3-3": { width: 14910, height: 60 },
      "MRV-L3-4": { width: 14910, height: 60 },
    },
  },
};

// ============================================================
// INTERCHANGE LED SPEC (single asset for all levels)
// ============================================================
export const INTERCHANGE_SPEC = {
  "MRV-INT": {
    label: "Marvel Interchange LED",
    uploadId: "MRV-INT",
    ledType: "interchange",
    contentGroup: "singleAsset",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-INT\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-INT",
    exactDuration: INTERCHANGE_DURATION,
    
    dimensions: [{ width: 3156, height: 80 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 3156,
      height: 80,
      duration: INTERCHANGE_DURATION,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 3156,
      height: 80,
    },
  },
};

// ============================================================
// DJ BOOTH SPEC (single asset)
// ============================================================
export const DJ_BOOTH_SPEC = {
  "MRV-DJ": {
    label: "DJ Booth",
    uploadId: "MRV-DJ",
    ledType: "dj-booth",
    contentGroup: "singleAsset",
    maxFiles: 1,
    requiredFiles: 1,
    filenamePattern: /(?:^|[_])MRV-DJ\.[^.]+$/i,
    filenameHint: "Filename must end with _MRV-DJ",
    exactDuration: DJ_BOOTH_DURATION,
    
    dimensions: [{ width: 1456, height: 312 }],
    
    videoSpec: {
      ...LED_VIDEO_COMMON,
      width: 1456,
      height: 312,
      duration: DJ_BOOTH_DURATION,
    },
    
    imageSpec: {
      ...LED_IMAGE_COMMON,
      width: 1456,
      height: 312,
    },
  },
};

// ============================================================
// ALL SPECS BY FORMAT
// ============================================================
export const SPECS_BY_FORMAT = {
  tiled: TILED_SPECS,
  overlay: OVERLAY_SPECS,
  "360": THREE_SIXTY_SPECS,
  interchange: INTERCHANGE_SPEC,
  "dj-booth": DJ_BOOTH_SPEC,
};

// ============================================================
// ALL SPECS (flat object for lookup)
// ============================================================
export const ALL_SPECS = {
  ...TILED_SPECS,
  ...OVERLAY_SPECS,
  ...THREE_SIXTY_SPECS,
  ...INTERCHANGE_SPEC,
  ...DJ_BOOTH_SPEC,
};

// ============================================================
// HELPER: Get segment ID from filename
// ============================================================
export function getSegmentIdFromFilename(filename, segments) {
  if (!segments) return null;
  
  const upper = filename.toUpperCase();
  
  return Object.keys(segments).find((segmentId) => {
    const re = new RegExp(`(?:^|[_])${segmentId}\\.[^.]+$`, "i");
    return re.test(upper);
  }) || null;
}

// ============================================================
// HELPER: Check if filename matches pattern
// ============================================================
export function validateFilename(filename, spec) {
  // For 360 specs with segments
  if (spec.segments) {
    const segmentId = getSegmentIdFromFilename(filename, spec.segments);
    return {
      valid: !!segmentId,
      segmentId,
      hint: spec.filenameHint,
    };
  }
  
  // For specs with explicit pattern
  if (spec.filenamePattern) {
    return {
      valid: spec.filenamePattern.test(filename),
      segmentId: null,
      hint: spec.filenameHint,
    };
  }
  
  // Fallback: check if ends with uploadId
  const re = new RegExp(`(?:^|[_])${spec.uploadId}\\.[^.]+$`, "i");
  return {
    valid: re.test(filename),
    segmentId: null,
    hint: `Filename must end with _${spec.uploadId}`,
  };
}

// ============================================================
// HELPER: Get dimensions for a file
// ============================================================
export function getDimensionsForFile(filename, spec) {
  // For 360 specs with segments
  if (spec.segments) {
    const segmentId = getSegmentIdFromFilename(filename, spec.segments);
    if (segmentId) {
      return spec.segments[segmentId];
    }
    return null;
  }
  
  // For specs with direct dimensions
  if (spec.dimensions?.length) {
    return spec.dimensions[0];
  }
  
  // Fallback to videoSpec dimensions
  if (spec.videoSpec?.width && spec.videoSpec?.height) {
    return { width: spec.videoSpec.width, height: spec.videoSpec.height };
  }
  
  return null;
}

// ============================================================
// HELPER: Build spec label for display
// ============================================================
export function buildSpecLabel(spec) {
  const parts = [];

  // Dimensions
  if (spec.segments) {
    const sizes = Object.values(spec.segments)
      .map((s) => `${s.width}×${s.height}`)
      .filter((v, i, a) => a.indexOf(v) === i); // unique
    parts.push(sizes.length > 3 ? "Various dimensions" : sizes.join(" / "));
  } else if (spec.dimensions?.length) {
    parts.push(spec.dimensions.map((d) => `${d.width}×${d.height}`).join(" / "));
  } else if (spec.videoSpec?.width && spec.videoSpec?.height) {
    parts.push(`${spec.videoSpec.width}×${spec.videoSpec.height}`);
  }

  // Video
  const videoSpec = spec.videoSpec || spec.commonVideoSpec;
  if (videoSpec) {
    // Container
    const exts = videoSpec.extensions || [];
    parts.push(exts.includes("mov") ? "MOV / MP4" : "MP4");

    // Codec
    const formats = videoSpec.formats || [];
    if (formats.includes("ProRes") && videoSpec.requireProRes4444) {
      parts.push(formats.includes("AVC") ? "H.264 / ProRes 4444" : "ProRes 4444");
    } else if (formats.includes("AVC")) {
      parts.push("H.264");
    }
    if (videoSpec.frameRate) {
      parts.push(`${videoSpec.frameRate}fps`);
    }
    
    // Duration
    if (spec.exactDuration) {
      parts.push(`Duration: ${spec.exactDuration}s exactly`);
    } else if (spec.durationRange) {
      parts.push(`Duration: ${formatDuration(spec.durationRange.min)} - ${formatDuration(spec.durationRange.max)}`);
    }
    
    parts.push("Looping");
  }

  // Image
  if (spec.imageSpec?.allowed) {
    parts.push("Static JPG / PNG");
    parts.push("RGB");
  }

  return parts.join(" · ");
}

// ============================================================
// HELPER: Format duration for display
// ============================================================
export function formatDuration(seconds) {
  if (typeof seconds !== "number" || isNaN(seconds)) return "";
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  if (mins > 0) {
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }
  return `${secs}s`;
}

// ============================================================
// METADATA WEBHOOK (LED-specific)
// ============================================================
export const METADATA_WEBHOOK = "https://hook.eu2.make.com/hs49hn7asl4ow6bx92kx1js28jpehkrt";

const marvelLedSpecs = {
  LED_FORMATS,
  SPECS_BY_FORMAT,
  ALL_SPECS,
  TILED_SPECS,
  OVERLAY_SPECS,
  THREE_SIXTY_SPECS,
  INTERCHANGE_SPEC,
  DJ_BOOTH_SPEC,
  getSegmentIdFromFilename,
  validateFilename,
  getDimensionsForFile,
  buildSpecLabel,
  formatDuration,
  METADATA_WEBHOOK,
};

export default marvelLedSpecs;
